import React, { useCallback, useState } from 'react'
import { settingListConfig, settingTitleList, settingsConfig } from 'src/assets/SettingsConfig'
import { SettingConfig } from 'src/lib/types/settings'
import { ArrowRight, User } from 'react-feather'
import Link from 'next/link'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import useSWR from 'swr'
import { useUser } from 'src/lib/contexts/UserContext'
import Permission from 'src/services/PermissionService'
import { useLocale } from 'src/lib/hooks/useLocal'
import { apiURI } from 'src/lib/utils/constants'
import { Check } from 'react-feather'
import { useSettingsPermissions } from 'src/lib/access-permission-hooks/useSettingsPermissions'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { OnboardingStep } from 'src/lib/types/onboardingV2'
const SettingsList: React.FC<{}> = ({}) => {
  const { user } = useUser()
  const { t } = useLocale()
  const { data: settingsResponse, isValidating } = useSWR(
    ['/md/merchants/integration-config', apiURI.FLO_KRATOS_CHECKOUT],
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const {
    state: organisationData,
    getters: { isOrgDetailsCompleted, isShopConnected }
  } = useOrganisationContext()

  const {
    canSetupSettings,
    canEditShopSettings,
    canEditAccountSettings,
    canEditInvoiceSettings,
    canAccessChannelSettings
  } = useSettingsPermissions()

  const { onboardingState } = organisationData

  const settingsToPermissionMap: { [key: string]: boolean } = {
    Setup: canSetupSettings,
    'Shop settings': canEditShopSettings,
    Account: canEditAccountSettings,
    Invoicing: canEditInvoiceSettings,
    Channels: canAccessChannelSettings
    // 'Payment settings':
  }

  const shouldBlockTileAccess = (type: string, index: number) => {
    if (type === 'organisational-details') return !isOrgDetailsCompleted && !isShopConnected
    if (type === 'payments')
      return !onboardingState.completed_states.includes(OnboardingStep.pg_setup) && !isShopConnected
    return (
      !settingsToPermissionMap[settingTitleList?.[index]] &&
      user?.isDemoUser &&
      (type === 'organisational-details' || type === 'members' || type === 'shop-details' || type === 'integrations')
    )
  }

  const shouldHideTheSections = (index: number) => {
    //hide all sections excep accountSection if isFromShopifyPublicApp
    if (organisationData.isFromShopifyPublicApp && settingTitleList?.[index] !== 'Account') return true
    return false
  }

  if (!isValidating && !Boolean(settingsResponse)) return <></>

  return (
    <>
      <div className='flex flex-row w-full gap-6'>
        <div className='w-3/5'>
          <ul className='flex flex-col w-full '>
            {(settingListConfig as any)?.map((settings: any, index: number) => {
              return shouldHideTheSections(index) ? (
                <></>
              ) : (
                <div className='' key={index}>
                  <div className='text-[#6e6e6e] text-sm	font-medium pt-2 pb-4 flex flex-row items-center gap-1'>
                    <div className='whitespace-nowrap	'>{settingTitleList?.[index]}</div>
                    <div className='h-[1px] bg-[#e0e0e0] w-full'> </div>
                  </div>

                  {(settings as any)?.map((setting: any) => {
                    const type = setting.type
                    const matchedSetting = (settingsResponse as any)?.find((setting: any) => setting?.type === type)

                    if (
                      !Boolean(matchedSetting) &&
                      type !== 'profile' &&
                      type !== 'activity-logs' &&
                      type !== 'organisational-details'
                    )
                      return <></>

                    if (matchedSetting?.type === 'members' && !Boolean(user?.isAdmin)) return <></>

                    const tagText = setting?.tagText
                    const redirectType =
                      setting?.type === 'billing-usage'
                        ? 'billing#subscriptions'
                        : setting?.type === 'integrations'
                        ? 'apps-integrations'
                        : `/settings/${setting?.type}`

                    return shouldBlockTileAccess(type, index) ? (
                      <li key={redirectType} className='pb-2 pointer-events-none opacity-50'>
                        <SettingItem
                          type={setting?.type ?? ''}
                          icon={setting?.icon}
                          tagText={tagText}
                          is_mandatory={matchedSetting?.is_mandatory ?? false}
                          label={setting?.label ?? 'NA'}
                          is_configured={matchedSetting?.is_configured ?? false}
                          permissionId={setting?.permissionId}
                          isIntegrationPage={!shouldBlockTileAccess(type, index)}
                        />
                      </li>
                    ) : (
                      <Link href={`/${redirectType}`} key={redirectType} passHref>
                        <li key={redirectType} className='pb-2'>
                          <SettingItem
                            type={setting?.type ?? ''}
                            icon={setting?.icon}
                            tagText={tagText}
                            is_mandatory={matchedSetting?.is_mandatory ?? false}
                            label={setting?.label ?? 'NA'}
                            is_configured={matchedSetting?.is_configured ?? false}
                            permissionId={setting?.permissionId}
                            isIntegrationPage={false}
                          />
                        </li>
                      </Link>
                    )
                  })}
                </div>
              )
            })}
          </ul>
        </div>
        <div className='w-2/5'>{/* <ProgressCard /> */}</div>
      </div>
    </>
  )
}

export const SettingItem: React.FC<SettingConfig> = ({
  type,
  icon: Icon,
  imgPath,
  tagText,
  tagColor = 'yellow',
  is_mandatory,
  is_configured,
  label,
  permissionId,
  isDisabled,
  isRedirect = false,
  isIntegrationPage = true,
  enabled = false
}) => {
  return (
    <Permission permissionId={permissionId}>
      <div
        className={`flex flex-row w-full px-4 py-3 ${
          isDisabled ? 'opacity-40' : 'bg-white hover:bg-gray-lightest'
        } cursor-pointer border-gray-light transition-all duration-200 delay-0`}
        data-trackable={`settings_${type}`}
      >
        <div className='flex flex-row items-center font-medium text-coal-dark gap-2 grow'>
          {Icon && <Icon size='16' />}
          {imgPath && (
            <img
              className='border-[1px] border-[#F0F0F0] rounded-[4px]'
              width={32}
              height={32}
              alt={type}
              src={imgPath}
            />
          )}
          <p className='text-sm'>{label}</p>
        </div>
        <div className='flex flex-row items-center gap-2'>
          {(!Boolean(is_configured) || isDisabled) && tagText && (
            <>
              <p
                className={`text-xs ${
                  tagColor === 'yellow'
                    ? 'text-yellow-dark settings_list-status_yellow bg-yellow-lighter'
                    : 'text-coal-light bg-[#FAFAFA]'
                }   px-1.5 py-0.5 rounded`}
              >
                {tagText}
              </p>
            </>
          )}
          {isIntegrationPage ? (
            Boolean(is_configured) && Boolean(enabled) ? (
              <div className='flex items-center py-[1px] bg-[#01cca71a] px-[6px] rounded-xl gap-0.5'>
                <Check size={12} className='text-primary-dark ' />
                <p className='text-primary-dark rounded-xl text-xs '>Active</p>
              </div>
            ) : (
              <div className='flex items-center py-[1px] bg-gray-lighter px-[6px] rounded-xl gap-0.5'>
                <p className='text-gray-dark rounded-xl text-xs '>Click to setup</p>
              </div>
            )
          ) : null}

          <ArrowRight size='16' className={`font-medium text-[#4D4D4D] ${isRedirect && '-rotate-45'}`} />
        </div>
      </div>
    </Permission>
  )
}

export default SettingsList
